// CIP fork feature (see FORK.md): this tab's view of the project's presence.
//
// A vanilla Zustand store, one per mounted project session. The heartbeat
// writes `entries`; pages write what they are about (`detail`) and the
// builder writes its focus and edits; components subscribe to the smallest
// slice they render.
import { createStore, type StoreApi } from "zustand/vanilla";
import {
  EDITING_WINDOW_MS,
  type PresenceEntry,
  type PresenceFocus,
  type PresenceLocation,
} from "../lib/presence";

/** What a page says about itself, beyond its path. */
export type PresenceDetail = {
  title?: string;
  resource?: PresenceLocation["resource"];
  view?: string | null;
  focus?: PresenceFocus | null;
};

export type PresenceRoomState = {
  /** Everyone's tabs from the last heartbeat, ours included. */
  entries: PresenceEntry[];
  /** The page's own description; cleared by the page on its way out. */
  detail: PresenceDetail | null;
  /** Until when this tab counts as editing (a keystroke or a drag). */
  editingUntil: number;
  /** Bumped whenever something a peer would see changed, so the heartbeat
   * goes out now rather than on its next tick. */
  changed: number;
  actions: {
    received: (entries: PresenceEntry[]) => void;
    setDetail: (detail: PresenceDetail | null) => void;
    patchDetail: (patch: PresenceDetail) => void;
    markEditing: (now?: number) => void;
  };
};

export type PresenceRoom = StoreApi<PresenceRoomState>;

export function createPresenceRoom(): PresenceRoom {
  return createStore<PresenceRoomState>((set, get) => ({
    entries: [],
    detail: null,
    editingUntil: 0,
    changed: 0,
    actions: {
      received: (entries) => set({ entries }),
      setDetail: (detail) => set({ detail, changed: get().changed + 1 }),
      patchDetail: (patch) => {
        const current = get().detail ?? {};
        const same = Object.entries(patch).every(
          ([key, value]) =>
            JSON.stringify(current[key as keyof PresenceDetail]) ===
            JSON.stringify(value),
        );
        if (same) return;
        set({ detail: { ...current, ...patch }, changed: get().changed + 1 });
      },
      markEditing: (now = Date.now()) => {
        const wasEditing = get().editingUntil > now;
        set({
          editingUntil: now + EDITING_WINDOW_MS,
          // Only the start of a burst is news; the rest rides the tick.
          ...(wasEditing ? {} : { changed: get().changed + 1 }),
        });
      },
    },
  }));
}
